import {
    Box,
    Button as MuiButton,
    Card,
    Stack,
    Typography,
} from '@mui/material';
import { Building2, Plus } from 'lucide-react';
import {
    ListBase,
    ListToolbar,
    Pagination,
    SearchInput,
    Title,
    TopToolbar,
    useGetIdentity,
    useListContext,
} from 'react-admin';
import { useState } from 'react';

import { AccountEmpty } from './AccountEmpty';
import { AccountCardGrid } from './AccountCardGrid';
import { AccountCreateDialog } from './AccountCreateDialog';

const accountFilters = [
    <SearchInput key="q" source="q" alwaysOn placeholder="Search accounts…" />,
];

const AccountListActions = ({
    onCreateClick,
}: {
    onCreateClick: () => void;
}) => (
    <TopToolbar>
        <MuiButton
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={onCreateClick}
            sx={{
                backgroundColor: '#10b981',
                '&:hover': {
                    backgroundColor: '#059669',
                },
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.8125rem',
            }}
        >
            New Account
        </MuiButton>
    </TopToolbar>
);

export const AccountList = () => {
    const { identity } = useGetIdentity();
    if (!identity) return null;
    return (
        <ListBase perPage={25} sort={{ field: 'name', order: 'ASC' }}>
            <AccountLayout />
        </ListBase>
    );
};

const AccountLayout = () => {
    const { data, isPending, filterValues } = useListContext();
    const hasSearch = filterValues?.q?.trim?.();
    const [dialogOpen, setDialogOpen] = useState(false);

    if (isPending) return null;
    if (!data?.length && !hasSearch) return <AccountEmpty />;

    return (
        <Stack component="div" sx={{ width: '100%' }}>
            <Title title="Accounts" />
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Building2 size={24} color="#10b981" />
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: '#0f172a',
                    }}
                >
                    Accounts
                </Typography>
            </Box>
            <ListToolbar
                filters={accountFilters}
                actions={
                    <AccountListActions
                        onCreateClick={() => setDialogOpen(true)}
                    />
                }
            />
            <Card
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: 'none',
                    p: 2,
                }}
            >
                <AccountCardGrid />
            </Card>
            <Box mt={1}>
                <Pagination rowsPerPageOptions={[12, 24, 48]} />
            </Box>
            <AccountCreateDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
            />
        </Stack>
    );
};
